/**
 * Utilidades para gestión de permisos basados en roles
 */

import { USER_ROLES, USER_ROLE_PERMISSIONS } from "./constants";

/**
 * Verifica si un rol tiene un permiso específico
 * @param {string} role - El rol del usuario (admin, operator, viewer)
 * @param {string} permission - El permiso a verificar (create, read, update, delete, manageUsers)
 * @returns {boolean} - true si el rol tiene el permiso, false en caso contrario
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;

  const rolePermissions = USER_ROLE_PERMISSIONS[role];
  if (!rolePermissions) return false;

  return rolePermissions[permission] === true;
};

/**
 * Verifica si un usuario puede eliminar (solo admin)
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede eliminar, false en caso contrario
 */
export const canDelete = (role) => {
  return hasPermission(role, "delete");
};

/**
 * Verifica si un usuario puede crear
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede crear, false en caso contrario
 */
export const canCreate = (role) => {
  return hasPermission(role, "create");
};

/**
 * Verifica si un usuario puede editar/actualizar
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede editar, false en caso contrario
 */
export const canUpdate = (role) => {
  return hasPermission(role, "update");
};

/**
 * Verifica si un usuario puede leer/ver
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede leer, false en caso contrario
 */
export const canRead = (role) => {
  return hasPermission(role, "read");
};

/**
 * Verifica si un usuario puede gestionar otros usuarios
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede gestionar usuarios, false en caso contrario
 */
export const canManageUsers = (role) => {
  return hasPermission(role, "manageUsers");
};

/**
 * Verifica si un usuario es administrador
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si es admin, false en caso contrario
 */
export const isAdmin = (role) => {
  return role === USER_ROLES.ADMIN;
};

/**
 * Verifica si un usuario es operador
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si es operator, false en caso contrario
 */
export const isOperator = (role) => {
  return role === USER_ROLES.OPERATOR;
};

/**
 * Verifica si un usuario es visualizador
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si es viewer, false en caso contrario
 */
export const isViewer = (role) => {
  return role === USER_ROLES.VIEWER;
};

/**
 * Verifica si un usuario tiene permisos de escritura (admin u operator)
 * @param {string} role - El rol del usuario
 * @returns {boolean} - true si puede escribir, false en caso contrario
 */
export const canWrite = (role) => {
  return canCreate(role) || canUpdate(role);
};

/**
 * Obtiene todos los permisos de un rol
 * @param {string} role - El rol del usuario
 * @returns {Object} - Objeto con todos los permisos del rol
 */
export const getPermissions = (role) => {
  return USER_ROLE_PERMISSIONS[role] || USER_ROLE_PERMISSIONS[USER_ROLES.VIEWER];
};

/**
 * Obtiene una lista de acciones permitidas para un rol
 * @param {string} role - El rol del usuario
 * @returns {Array<string>} - Array con los nombres de las acciones permitidas
 */
export const getAllowedActions = (role) => {
  const permissions = getPermissions(role);
  return Object.keys(permissions).filter((key) => permissions[key] === true);
};

/**
 * Verifica si un usuario puede realizar múltiples acciones
 * @param {string} role - El rol del usuario
 * @param {Array<string>} permissions - Array de permisos a verificar
 * @returns {boolean} - true si tiene todos los permisos, false en caso contrario
 */
export const hasAllPermissions = (role, permissions) => {
  if (!Array.isArray(permissions)) return false;
  return permissions.every((permission) => hasPermission(role, permission));
};

/**
 * Verifica si un usuario puede realizar al menos una de las acciones
 * @param {string} role - El rol del usuario
 * @param {Array<string>} permissions - Array de permisos a verificar
 * @returns {boolean} - true si tiene al menos un permiso, false en caso contrario
 */
export const hasAnyPermission = (role, permissions) => {
  if (!Array.isArray(permissions)) return false;
  return permissions.some((permission) => hasPermission(role, permission));
};

/**
 * Filtra opciones de menú basándose en los permisos del usuario
 * @param {Array<Object>} menuItems - Array de items del menú con propiedad requiredPermission
 * @param {string} role - El rol del usuario
 * @returns {Array<Object>} - Array filtrado de items del menú
 */
export const filterMenuByPermissions = (menuItems, role) => {
  return menuItems.filter((item) => {
    if (!item.requiredPermission) return true;

    if (Array.isArray(item.requiredPermission)) {
      return hasAnyPermission(role, item.requiredPermission);
    }

    return hasPermission(role, item.requiredPermission);
  });
};

/**
 * Obtiene un mensaje descriptivo de las limitaciones del rol
 * @param {string} role - El rol del usuario
 * @returns {string} - Mensaje descriptivo de limitaciones
 */
export const getRoleLimitationMessage = (role) => {
  const messages = {
    [USER_ROLES.ADMIN]: "Tienes acceso completo a todas las funciones del sistema.",
    [USER_ROLES.OPERATOR]:
      "Puedes crear, ver y editar elementos. Para eliminar necesitas un token de autorización del administrador.",
    [USER_ROLES.VIEWER]: "Solo puedes visualizar información, sin poder modificarla.",
  };

  return messages[role] || "Rol no reconocido.";
};

/**
 * Verifica si un usuario puede acceder a una ruta específica
 * @param {string} role - El rol del usuario
 * @param {string} route - Nombre de la ruta
 * @returns {boolean} - true si puede acceder, false en caso contrario
 */
export const canAccessRoute = (role, route) => {
  const routePermissions = {
    users: "manageUsers",
    dashboard: "read",
    plants: "read",
    processes: "read",
    rooms: "read",
    machines: "read",
    catalogs: "read",
  };

  const requiredPermission = routePermissions[route];
  if (!requiredPermission) return true; // Rutas sin restricción

  return hasPermission(role, requiredPermission);
};

export default {
  hasPermission,
  canDelete,
  canCreate,
  canUpdate,
  canRead,
  canManageUsers,
  isAdmin,
  isOperator,
  isViewer,
  canWrite,
  getPermissions,
  getAllowedActions,
  hasAllPermissions,
  hasAnyPermission,
  filterMenuByPermissions,
  getRoleLimitationMessage,
  canAccessRoute,
};
